import { type Dispatch, type SetStateAction } from 'react';

import Link from 'next/link';

import { jwtDecode } from 'jwt-decode';

import { getNowInSeconds, useSetCredentials } from '@quick/auth';
import { buttonCStyles } from '@quick/components/Button';
import { NewLogin } from '@quick/components/NewLogin';
import { useToast } from '@quick/components/Toaster';
import type { LoginRes } from '@quick/go-api-types/users/v2/login';
import { useRecaptcha } from '@quick/utils';

import { useAppTranslation } from '@/app/i18n/client';
import { envs } from '@/layers/core/data/const';
import msgFromAxiosException from '@/layers/core/deal/mappers/msgFromAxiosException';
import useRequestConfigHeaders from '@/layers/core/deal/services/useRequestConfig';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import { useLangProvider } from './AppLayout';
import { TwoFactorValidation } from './TwoFactorValidation';

type TF = Omit<LoginRes, 'two_factor'> | null;
interface ILogin {
  twoFactor: [TF, Dispatch<SetStateAction<TF>>];
  onSuccess?: () => void;
  className?: string;
  tfClassName?: string;
}

export function LoginComponent({
  twoFactor,
  onSuccess,
  className,
  tfClassName,
}: ILogin) {
  const lang = useLangProvider();
  const setCredentials = useSetCredentials();
  const { toast } = useToast();
  const [twoFactorToken, setTwoFactorToken] = twoFactor;

  const { t } = useAppTranslation(['common', 'dl']);
  const getKey = useRecaptcha(envs.grecaptchaApiKey);
  const headers = useRequestConfigHeaders();
  if (twoFactorToken)
    return (
      <TwoFactorValidation
        className={tfClassName}
        token={twoFactorToken}
        goBack={() => setTwoFactorToken(null)}
        onSuccess={onSuccess}
      />
    );
  return (
    <NewLogin
      className={className}
      onValid={async (data) => {
        try {
          const google_response_token = await getKey();
          const res = await axios_demand.post<LoginRes>(
            '/users/v2/login',
            {
              credential: data.mobile_phone,
              password: data.password,
              google_response_token,
            },
            {
              headers,
            }
          );

          const token = res.data;
          if (res.data.two_factor) return setTwoFactorToken(token);

          setCredentials({
            access: token.access_token,
            refresh: token.refresh_token,
          });

          const decoded = jwtDecode<{
            iat: number;
          }>(token.access_token);
          const userLoginTimeStamp = decoded?.iat;
          if (!userLoginTimeStamp) return;

          const differenceBetweenLoginAndNow =
            getNowInSeconds() - userLoginTimeStamp;
          const minutes = Math.abs(differenceBetweenLoginAndNow / 60);

          if (minutes >= 1) {
            toast({
              title: t('dl:timeOutOfSync.title'),
              variant: 'warning',
              description: t('dl:timeOutOfSync.description'),
              duration: 20000,
            });
          }

          onSuccess?.();
        } catch (err) {
          toast({
            title: t('dl:loginError'),
            description: msgFromAxiosException(err).msg,
            variant: 'danger',
            duration: 3000,
          });
        }
      }}
      recover={
        <Link
          href={`/${lang}/account/recovery`}
          className="dark:text-brand dark:hover:text-brand-dark text-center font-semibold
            hover:underline"
        >
          {t('dl:recovery')}
        </Link>
      }
      signUp={
        <Link
          href={`/${lang}/account/register-user`}
          className={buttonCStyles({
            variant: 'reject',
            className: 'rounded-full px-8',
          })}
        >
          {t('common:signUp')}
        </Link>
      }
      copies={{
        signIn: t('dl:login.signInTitle') as [string, string],
        newCustomer: t('dl:login.newCustomer') as [string, string],
        description: t('dl:login.description'),
        login: t('dl:login.titleButton'),
      }}
    />
  );
}

/*
copies = {
  title: t('dl:login.title'),
  description: t('dl:login.description'),
  labelEmail: t('dl:login.labelEmail'),
  placeholderEmail: t('dl:login.placeholderEmail'),
  labelPass: t('dl:login.labelPass'),
  placeholderPass: t('dl:login.placeholderPass'),
  labelRemind: t('dl:login.labelRemind'),
  titleButton: t('dl:login.titleButton'),
};
*/
